export const BAKED_EFFECT_KINDS = ['shadow', 'glow', 'frame-below', 'frame-above'] as const;

export type BakedEffectKind = (typeof BAKED_EFFECT_KINDS)[number];

export interface BakedEffectTile {
  readonly owner: string;
  readonly kind: BakedEffectKind;
  readonly index: number;
}

const BAKED_EFFECT_TILE_ID = new RegExp(`^(.+)-(${BAKED_EFFECT_KINDS.join('|')})-(\\d+)$`);

// Packs parsed before the shared vocabulary name a clipping mask's tiles `<slot>-clipfx-below|above-N`; their
// owner is the mask layer `<slot>-clipbase`, not the slot the name starts with.
const LEGACY_CLIP_TILE_ID = /^(.+)-clipfx-(below|above)-(\d+)$/;

// A grown seat (vignette fit grid) clones the cell with one `-fit-N` suffix on every box: the tile
// `<owner>-<kind>-<i>-fit-N` belongs to `<owner>-fit-N`. A continuation page clones seat 0 once more with
// `-l<i>`/`-r<i>` (packPageGrid): `<owner>-<kind>-<i>-fit-0-l3` belongs to `<owner>-fit-0-l3`.
const GROWN_SEAT_SUFFIX = /-fit-\d+(?:-[lr]\d+)?$/;

export function bakedEffectTileId(owner: string, kind: BakedEffectKind, index: number): string {
  return `${owner}-${kind}-${index}`;
}

// The id is the only link from a baked tile to the layer it belongs to.
export function parseBakedEffectTileId(tileId: string): BakedEffectTile | undefined {
  const seat = GROWN_SEAT_SUFFIX.exec(tileId)?.[0] ?? '';
  const id = tileId.slice(0, tileId.length - seat.length);
  const legacy = LEGACY_CLIP_TILE_ID.exec(id);
  if (legacy) {
    const kind = legacy[2] === 'below' ? 'frame-below' : 'frame-above';
    return { owner: `${legacy[1]}-clipbase${seat}`, kind, index: Number(legacy[3]) };
  }

  const parts = BAKED_EFFECT_TILE_ID.exec(id);
  return parts ? { owner: parts[1] + seat, kind: parts[2] as BakedEffectKind, index: Number(parts[3]) } : undefined;
}

type Rect4 = readonly [number, number, number, number];

// A baked tile follows its owner's frame: same offset and size, scaled by the owner's axis ratios. All rects
// in one space (collage). Undefined for an empty `from` — nothing to scale against.
export function refitBakedTileRect(tile: Rect4, from: Rect4, to: Rect4): [number, number, number, number] | undefined {
  if (from[2] <= 0 || from[3] <= 0) return undefined;
  const sx = to[2] / from[2];
  const sy = to[3] / from[3];
  return [to[0] + (tile[0] - from[0]) * sx, to[1] + (tile[1] - from[1]) * sy, tile[2] * sx, tile[3] * sy];
}

/** What a box needs to show its kinship: both a template box and a render box fit. */
interface BakedKinBox {
  readonly id: string;
  readonly type: string;
  readonly idFromTemplate?: string;
}

/**
 * Owner of every baked tile in ONE collage: `tile box id → owner box id`, over all boxes with group children.
 * The only link is the `idFromTemplate` provenance, and it is not unique: a copy of a photo (⌘D) keeps the
 * original's key. So an owner is a key met exactly once in the collage; on a repeat the tile belongs to nobody
 * and stands still. The commit and the live drag preview read this one answer.
 */
export function bakedTileOwners<B extends BakedKinBox>(
  boxes: readonly B[],
  childrenOf: (box: B) => readonly B[] | undefined,
): Map<string, string> {
  const leaves: B[] = [];
  const walk = (list: readonly B[]): void => {
    for (const box of list) {
      const children = childrenOf(box);
      if (children) walk(children);
      else leaves.push(box);
    }
  };

  walk(boxes);
  const ownerByKey = new Map<string, string | null>();
  for (const box of leaves) {
    if (!box.idFromTemplate) continue;
    ownerByKey.set(box.idFromTemplate, ownerByKey.has(box.idFromTemplate) ? null : box.id);
  }

  const out = new Map<string, string>();
  for (const box of leaves) {
    const tile = box.type === 'object' && box.idFromTemplate ? parseBakedEffectTileId(box.idFromTemplate) : undefined;
    const owner = tile ? ownerByKey.get(tile.owner) : undefined;
    if (owner && owner !== box.id) out.set(box.id, owner);
  }

  return out;
}
